import { Chip } from "@mui/material";
import { styled } from "@mui/material";
import { GridColDef, GridRenderCellParams, GridRowModel } from "@mui/x-data-grid";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { groupBy, round, truncate } from "lodash";
import { useMemo } from "react";

import { Typography } from "@/Components/v2/atoms/Typography";
import { DataGrid } from "@/Components/v2/organism/DataGrid";
import { useDefaultNodeNameInfo } from "@/hooks/shared/useDefaultNodeName";
import { useConfig } from "@/hooks/useConfig";

import { bcpLayerKey, BCPNode, getBusinessUnitName } from "@/pages/scenario-builder/v3/utils/bcp";
import {
  RISK_SMALL_STYLE,
  RISK_MEDIUM_STYLE,
  RISK_LARGE_STYLE,
} from "@/pages/scenario-builder/types";
import { AllocationStrategyResponse } from "@mutiny-group/sbo-openapi-client";
import { Stack } from "@/Components/v2/atoms/Stack";

type HistoricSpendTablesProps = {
  allocationStrategy?: AllocationStrategyResponse;
  distinctBCP?: Record<string, BCPNode>;
};

type AllocationShiftRow = {
  id: string;
  budget: string;
  input: number | null;
  historicSpend: number | null;
  change: number | null;
  isHeader: boolean;
};

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: "none !important",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: theme.palette.mode === "dark" ? "#2E2E2E" : "#EEF3FD",
    border: "none !important",
    borderRadius: "0px !important",
  },
  "& .MuiDataGrid-row": {
    cursor: "default",
  },
  "& .MuiDataGrid-row:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
  "& .MuiDataGrid-row.Mui-selected": {
    backgroundColor: "transparent",
  },
  "& .MuiDataGrid-row.Mui-selected:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #E5E7EB !important",
  },
  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
    outline: "none",
  },
  "& .MuiDataGrid-columnHeader:last-child": {
    paddingRight: "0px",
  },
  "& .MuiDataGrid-cell:last-child": {
    paddingRight: "0px",
  },
  // Disable column header interactions
  "& .MuiDataGrid-columnHeader": {
    pointerEvents: "none",
  },
  // Hide sort indicators
  "& .MuiDataGrid-iconButtonContainer": {
    display: "none",
  },
  "& .MuiDataGrid-sortIcon": {
    display: "none",
  },
  "& .MuiDataGrid-columnHeaderTitleContainer": {
    "& .MuiDataGrid-iconButtonContainer": {
      display: "none",
    },
  },
}));

const HistoricSpendChangeTables = ({
  allocationStrategy,
  distinctBCP,
}: HistoricSpendTablesProps) => {
  const apiRef = useGridApiRef();
  const { config } = useConfig();
  const { formatCurrency } = config.currency;
  const { mapBcpName } = useDefaultNodeNameInfo();

  const getChangeStyle = (value: number): { backgroundColor: string; color?: string } => {
    const absValue = Math.abs(value);
    if (absValue < 1) return RISK_SMALL_STYLE;
    if (absValue >= 1 && absValue <= 5) return RISK_MEDIUM_STYLE;
    return RISK_LARGE_STYLE;
  };

  // Group allocationShiftDetails by BCP key
  // Note: Use null-coalescing to match how bcpKeyToNode keys are built in ScenarioResultsProvider
  const allocationDetailsByBCPKey = useMemo(() => {
    if (!allocationStrategy?.allocationShiftDetails) return {};
    return groupBy(allocationStrategy.allocationShiftDetails, (detail) =>
      bcpLayerKey({
        brand: detail.brand ?? "",
        category: detail.category ?? "",
        product: detail.product ?? "",
      })
    );
  }, [allocationStrategy?.allocationShiftDetails]);

  const budgetChangeRows = useMemo(() => {
    if (!allocationStrategy) return [];
    return [
      {
        id: "total",
        budget: "Total",
        input: allocationStrategy.budget,
        historicSpend: allocationStrategy.historicalBudget,
        change: allocationStrategy.overallBudgetChange,
        isHeader: false,
      },
    ];
  }, [allocationStrategy]);

  const channelAllocationRows = useMemo(() => {
    if (!distinctBCP) return [];

    const rows: AllocationShiftRow[] = [];
    let rowIndex = 0;

    Object.entries(allocationDetailsByBCPKey).forEach(([bcpKey, details]) => {
      const bcp = distinctBCP[bcpKey];
      if (!bcp) return;

      const mappedBcp = mapBcpName(bcp);
      const businessUnitName = getBusinessUnitName(mappedBcp);

      // Add header row for business unit
      rows.push({
        id: `header-${rowIndex++}`,
        budget: businessUnitName,
        input: null,
        historicSpend: null,
        change: null,
        isHeader: true,
      });

      // Add detail rows for each channel
      details.forEach((detail) => {
        const channelDisplay =
          detail.channel?.toLowerCase() === "all" ? "All Channels" : detail.channel;
        const channelLabel = detail.geography
          ? `${detail.geography} - ${channelDisplay}`
          : channelDisplay;

        rows.push({
          id: `detail-${rowIndex++}`,
          budget: channelLabel,
          input: detail.spend,
          historicSpend: detail.historicalSpend,
          change: detail.allocationShift,
          isHeader: false,
        });
      });
    });

    return rows;
  }, [allocationDetailsByBCPKey, distinctBCP, mapBcpName]);

  const columns: GridColDef[] = [
    {
      field: "budget",
      headerName: "Budget",
      flex: 2,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<GridRowModel, string>) => {
        const row = params.row as AllocationShiftRow;
        return (
          <Typography variant="body2" sx={{ fontWeight: row.isHeader ? 600 : 400 }}>
            {truncate(params.value, { length: 48 })}
          </Typography>
        );
      },
    },
    {
      field: "input",
      headerName: "Input",
      flex: 1,
      renderCell: (params: GridRenderCellParams<GridRowModel, number | null>) => {
        const row = params.row as AllocationShiftRow;
        if (row.isHeader || params.value == null) return null;
        return formatCurrency(params.value);
      },
    },
    {
      field: "historicSpend",
      headerName: "Historic Spend",
      flex: 1,
      renderCell: (params: GridRenderCellParams<GridRowModel, number | null>) => {
        const row = params.row as AllocationShiftRow;
        if (row.isHeader || params.value == null) return null;
        return formatCurrency(params.value);
      },
    },
    {
      field: "change",
      headerName: "Change",
      renderCell: (params: GridRenderCellParams<GridRowModel, number | null>) => {
        const row = params.row as AllocationShiftRow;
        if (row.isHeader) return null;
        if (!params.value && params.value !== 0) return "N/A";

        const percentValue = params.value * 100;
        const { backgroundColor, color } = getChangeStyle(percentValue);

        return (
          <Chip
            label={`${round(percentValue, 2)}%`}
            size="small"
            sx={{
              ...(color && { color }),
              backgroundColor,
              fontWeight: 600,
              borderRadius: 1,
            }}
          />
        );
      },
    },
  ];

  if (!allocationStrategy) return null;
  const hasAllocationDetails = channelAllocationRows.length > 0;

  return (
    <Stack gap={3}>
      <Typography fontWeight={600}>Total Budget Change</Typography>
      <StyledDataGrid
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableMultipleRowSelection
        rows={budgetChangeRows}
        columns={columns}
        apiRef={apiRef}
        hideFooter
        autoHeight
        density="compact"
      />

      {hasAllocationDetails && (
        <>
          <Typography fontWeight={600}>Channel Allocation Shift</Typography>
          <StyledDataGrid
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            disableMultipleRowSelection
            rows={channelAllocationRows}
            columns={columns}
            hideFooter
            autoHeight
            density="compact"
            getRowClassName={(params) =>
              (params.row as AllocationShiftRow).isHeader ? "header-row" : ""
            }
          />
        </>
      )}
    </Stack>
  );
};

export default HistoricSpendChangeTables;
